/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select from 'react-select';
import { FixedSizeList } from 'react-window';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { calendarIcons } from '../../../../shared/constants';

const IconContainer = styled('div')`
  height: 25px;
  width: 25px;
  border-radius: 4px;
  color: ${(props) => props.color || '#444444'};
  background: ${(props) => props.backgroundColor || 'transparent'};
  border: ${(props) => (props.backgroundColor ? `1px solid ${props.backgroundColor}` : 'none')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  padding: 4px;
`;

// Custom Option Component with icon (left), name (middle), video icon (right)
const CustomOption = (props) => {
  const {
    innerRef,
    innerProps,
    data,
    isFocused,
  } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: isFocused ? '#F5F5F5' : 'white',
        fontFamily: 'Nunito Sans',
        fontSize: '16px',
        color: '#444444',
      }}
    >
      {/* Left: Icon + Name */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <IconContainer
          color={data.iconColor}
          backgroundColor={data.iconBackgroundColor}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon={data.icon}
            color={data.iconColor || '#444444'}
          />
        </IconContainer>
        <span>{data.name}</span>
      </div>

      {/* Right: Video icon if media exists */}
      {data.media?.length > 0 && (
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='video-camera'
          color='#9E9E9E'
          style={{ marginLeft: 5, minWidth: 16 }}
        />
      )}
    </div>
  );
};

// Virtualized MenuList component using react-window
const MenuList = (props) => {
  const {
    options,
    children,
    maxHeight,
    getValue,
  } = props;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * 40; // 40px is the item height

  if (!children || children.length === 0) {
    return null;
  }

  const height = Math.min(maxHeight, children.length * 40);

  return (
    <FixedSizeList
      height={height}
      itemCount={children.length}
      itemSize={40}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </FixedSizeList>
  );
};

const SelectDropdownExercisePickerLocal = ({
  listItems,
  defaultValue,
  selectedOption,
  onExerciseChange,
  placeholder,
  typeToFilter,
  isTags,
}) => {
  // Build all exercise options (accessories, tags, exercises)
  // Filter by type if typeToFilter is provided
  const exerciseOptions = (listItems || [])
    .filter((exercise) => !typeToFilter || exercise.type === typeToFilter)
    .map((exercise) => ({
      ...exercise,
      name: exercise.name,
      exerciseId: exercise.id,
      icon: isTags ? 'tag' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.icon,
      iconColor: isTags ? '#FF4500' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.iconColor,
      iconBackgroundColor: isTags ? 'rgba(255, 69, 0, 0.2)' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.containerColor,
    }));

  const currentExercise = selectedOption || defaultValue;

  return (
    <Select
      options={exerciseOptions}
      value={currentExercise}
      onChange={onExerciseChange}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
        option: (provided, state) => ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: state.isFocused ? '#F5F5F5' : 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '16px',
          color: '#444444',
        }),
      }}
      components={{
        Option: CustomOption,
        MenuList,
      }}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.exerciseId}
      placeholder={placeholder || 'Select exercise...'}
    />
  );
};

SelectDropdownExercisePickerLocal.propTypes = {
  listItems: PropTypes.arrayOf(PropTypes.shape({})),
  defaultValue: PropTypes.shape({}),
  selectedOption: PropTypes.shape({}),
  onExerciseChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  typeToFilter: PropTypes.string,
  isTags: PropTypes.bool.isRequired,
};

SelectDropdownExercisePickerLocal.defaultProps = {
  listItems: [],
  defaultValue: null,
  selectedOption: null,
  placeholder: 'Select exercise...',
  typeToFilter: null,
};

CustomOption.propTypes = {
  innerRef: PropTypes.func,
  innerProps: PropTypes.shape({}),
  data: PropTypes.shape({
    name: PropTypes.string,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    iconBackgroundColor: PropTypes.string,
    media: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isFocused: PropTypes.bool,
};

CustomOption.defaultProps = {
  innerRef: null,
  innerProps: {},
  data: {},
  isFocused: false,
};

MenuList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})),
  children: PropTypes.node,
  maxHeight: PropTypes.number,
  getValue: PropTypes.func,
};

MenuList.defaultProps = {
  options: [],
  children: null,
  maxHeight: 300,
  getValue: () => [null],
};

export default SelectDropdownExercisePickerLocal;
